/**
 * Post-mount enhancements applied to a Room3DPreview viewer instance so
 * the live PDP's interactive 3D matches the look of the pre-baked hero
 * raster. Single source of truth for both:
 *   - the offline bake harness (scripts/bake-preset-rasters/harness.html)
 *   - the live readymade-frame PDP (assets/readymade-frame-pdp.js)
 *
 * If the look here drifts, the static raster won't match the 3D viewer
 * any more — and the user will see a jarring jump when entering 3D mode.
 *
 * Exposed as `window.PreviewEnhance.applyToInstance(instance, options)`.
 *
 * What it does, in order:
 *   1. Camera yaw — rotate around the frame centroid so the moulding
 *      profile is visible (default 25°). Pivot is the frame's world
 *      position, not (0,0,0).
 *   2. Material tweaks — boost envmap intensity, drop it for saturated
 *      colours so chroma frames keep their punch, warm the mount window.
 *   3. Shadow + AO — re-paint the cast-shadow + contact-AO canvas
 *      textures darker (matrixAutoUpdate is locked, so .scale is a no-op).
 *   4. Plaster wall — replace the backdrop's flat colour fill with a
 *      procedural plaster texture (noise + blotches + vignette).
 */
(function () {
  'use strict';

  function applyToInstance(instance, options) {
    if (!instance) return;
    const opts = options || {};
    const yawDeg     = opts.yawDeg     ?? 25;
    const pitchDeg   = opts.pitchDeg   ?? 0;
    const envBoost   = opts.envBoost   ?? 1.6;
    const mountWarm  = opts.mountWarm  ?? '#fbf8f3';
    const wallNoise  = opts.wallNoise  ?? 0.18;
    const shadowDarken = opts.shadowDarken ?? 0.30;
    const wallVignette = opts.wallVignette ?? 0.14;
    const skipCamera   = opts.skipCamera === true;
    // `skipBackdrop` — used on the live PDP, where the static hero raster
    // (visible underneath the canvas) is the wall. We want the camera +
    // material + shadow tweaks but we don't paint a plaster texture nor
    // tone-mapping-bump because the wall isn't ours to render.
    const skipBackdrop = opts.skipBackdrop === true;

    const viewer = instance.viewer;
    if (!viewer) return;

    // ── 0. Force the renderer to fill its container ──────────────────
    // The bundle's `_fitCanvas` only triggers via ResizeObserver, which
    // can no-op if the container's size hasn't changed since the canvas
    // was created at its 600×300 default. The result on the live PDP is
    // a 2:1 canvas dropped into a 1:1 stage — bottom half of the stage
    // shows the parent background, looks like a hard dark line.
    //
    // Run on next frame so the container has fully laid out, AND retry
    // unconditionally. Re-running setSize with same dims is a cheap no-op.
    const fitToContainer = () => {
      try {
        const dom = viewer.renderer?.domElement;
        const container = dom?.parentElement;
        if (!dom || !container) return;
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (w <= 0 || h <= 0) return;
        viewer.renderer.setSize(w, h, true);
        if (viewer.camera) {
          viewer.camera.aspect = w / h;
          viewer.camera.updateProjectionMatrix();
        }
        if (typeof viewer.composer?.setSize === 'function') {
          viewer.composer.setSize(w, h);
        }
      } catch (e) { /* non-fatal */ }
    };
    fitToContainer();
    // The stage often has clientWidth/Height = 0 at mount time (hidden
    // until the user enters 3D mode). Observe the container so the moment
    // it becomes visible / sized, we refit. Self-disposing once we hit a
    // good size.
    try {
      const dom = viewer.renderer?.domElement;
      const container = dom?.parentElement;
      if (container && typeof ResizeObserver === 'function') {
        let ro = null;
        ro = new ResizeObserver(() => {
          if (container.clientWidth > 0 && container.clientHeight > 0) {
            fitToContainer();
            // Keep observing — stage size may change again if the user
            // resizes the window. Cheap.
          }
        });
        ro.observe(container);
      }
    } catch (e) { /* non-fatal */ }

    const cam = viewer.camera;
    const Vector3 = cam.position.constructor;

    // ── 1. Camera yaw around frame centroid ──────────────────────────
    if (!skipCamera) {
      const frameSlot = viewer.frameSlots?.[0];
      const target = new Vector3(0, 0, 0);
      if (frameSlot?.frameGroup) {
        frameSlot.frameGroup.updateMatrixWorld?.(true);
        frameSlot.frameGroup.getWorldPosition?.(target);
      }
      const offset = new Vector3().subVectors(cam.position, target);
      const yawRad   = yawDeg   * Math.PI / 180;
      const pitchRad = pitchDeg * Math.PI / 180;
      const cy = Math.cos(yawRad), sy = Math.sin(yawRad);
      const x1 = offset.x * cy + offset.z * sy;
      const z1 = -offset.x * sy + offset.z * cy;
      const dist = Math.hypot(x1, z1);
      const cp = Math.cos(pitchRad), sp = Math.sin(pitchRad);
      const y1 = offset.y * cp + dist * sp;
      const r2 = dist * cp - offset.y * sp;
      const ratio = dist > 1e-6 ? r2 / dist : 1;
      cam.position.set(target.x + x1 * ratio, target.y + y1, target.z + z1 * ratio);
      cam.lookAt(target);
      cam.updateProjectionMatrix();
    }

    // ── 1b. Warm + dim the ambient light ─────────────────────────────
    // The bundle's orbit-mode ambient is pure white (#ffffff) at intensity
    // 0.55. Combined with a 3800K directional key, the diffuse fill on
    // mounts and the wall stays neutral and reads cold next to the
    // Gemini-baked hero raster. Tint the ambient toward incandescent and
    // pull intensity down so the overall scene is muted closer to the
    // raster's exposure.
    if (viewer.ambientLight) {
      viewer.ambientLight.color?.set?.('#fff2dc');
      viewer.ambientLight.intensity = 0.40;
    }
    // Tone-mapping exposure: ACESFilmicToneMapping at exposure 1.0 was
    // blowing out highlights on white frames. 0.85 brings the overall
    // brightness closer to the static hero raster's gentler photographic
    // exposure without crushing midtones.
    if (viewer.renderer) viewer.renderer.toneMappingExposure = 0.85;

    // ── 2. Material tweaks (envmap, mount warm) ──────────────────────
    const frameGroup = viewer.frameSlots?.[0]?.frameGroup;
    if (frameGroup) {
      const hsl = { h: 0, s: 0, l: 0 };
      frameGroup.traverse((obj) => {
        if (!obj.material) return;
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats) {
          if (!m) continue;
          let baseSat = 0;
          if (m.color?.getHSL) { m.color.getHSL(hsl); baseSat = hsl.s; }

          if ('envMapIntensity' in m) {
            // White envmap reflection desaturates strong colours; drop
            // intensity proportionally to base saturation.
            const desatPenalty = 1 - 0.75 * Math.min(1, Math.max(0, baseSat));
            m.envMapIntensity = (m.envMapIntensity ?? 1) * envBoost * desatPenalty;
            m.needsUpdate = true;
          }

          if (m.color && !m.map && obj.name?.toLowerCase().includes('mount')) {
            m.color.set(mountWarm);
            m.needsUpdate = true;
          }
        }
      });
    }

    // ── 3. Cast shadow + contact AO ──────────────────────────────────
    const slot = viewer.frameSlots?.[0];
    if (slot?.shadowQuad?.material?.map?.image && shadowDarken < 1) {
      darkenCanvasTexture(slot.shadowQuad.material.map, shadowDarken, 1);
    }
    if (slot?.aoQuad) {
      if (slot.aoQuad.material?.map?.image) {
        darkenCanvasTexture(slot.aoQuad.material.map, shadowDarken, 1);
      }
      if (slot.aoQuad.material) {
        slot.aoQuad.material.opacity = Math.min(1, (slot.aoQuad.material.opacity ?? 0.65) * 1.4);
        slot.aoQuad.material.needsUpdate = true;
      }
    }

    // ── 4. Plaster wall texture (skipped when the caller paints the wall) ──
    if (!skipBackdrop) {
      try {
        const CanvasTexture = window.THREE?.CanvasTexture;
        const backdrop = viewer.backdrop;
        if (CanvasTexture && backdrop?.material && (wallNoise > 0 || wallVignette > 0)) {
          const baseColor = opts.wallColor
            ? hexToRgbNorm(opts.wallColor)
            : { r: backdrop.material.color.r, g: backdrop.material.color.g, b: backdrop.material.color.b };
          const tex = makePlasterTexture(CanvasTexture, baseColor, wallNoise, wallVignette);
          backdrop.material.map = tex;
          backdrop.material.color.setRGB(1, 1, 1);
          backdrop.material.needsUpdate = true;
        }
      } catch (e) {
        console.warn('[preview-3d-enhance] plaster texture skipped:', e.message);
      }
    }
  }

  function hexToRgbNorm(hex) {
    const m = String(hex).trim().replace(/^#/, '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
    if (!m) return { r: 0.96, g: 0.94, b: 0.91 }; // fallback to cream
    let s = m[1];
    if (s.length === 3) s = s.split('').map((c) => c + c).join('');
    const n = parseInt(s, 16);
    return { r: ((n >> 16) & 0xff) / 255, g: ((n >> 8) & 0xff) / 255, b: (n & 0xff) / 255 };
  }

  function darkenCanvasTexture(tex, rgbScale, alphaScale) {
    const img = tex.image;
    const ctx = img.getContext?.('2d');
    if (!ctx) return;
    const id = ctx.getImageData(0, 0, img.width, img.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i]     = Math.round(d[i]     * rgbScale);
      d[i + 1] = Math.round(d[i + 1] * rgbScale);
      d[i + 2] = Math.round(d[i + 2] * rgbScale);
      if (alphaScale !== 1) d[i + 3] = Math.min(255, Math.round(d[i + 3] * alphaScale));
    }
    ctx.putImageData(id, 0, 0);
    tex.needsUpdate = true;
  }

  function makePlasterTexture(CanvasTexture, baseColor, amplitude, vignetteStrength) {
    const SIZE = 1024;
    const c = document.createElement('canvas');
    c.width = SIZE; c.height = SIZE;
    const ctx = c.getContext('2d');
    const baseR = Math.round(baseColor.r * 255);
    const baseG = Math.round(baseColor.g * 255);
    const baseB = Math.round(baseColor.b * 255);
    ctx.fillStyle = `rgb(${baseR},${baseG},${baseB})`;
    ctx.fillRect(0, 0, SIZE, SIZE);

    if (amplitude > 0) {
      const img = ctx.getImageData(0, 0, SIZE, SIZE);
      const data = img.data;
      const ampHi = amplitude * 0.55 * 255;
      for (let i = 0; i < data.length; i += 4) {
        const n = (Math.random() - 0.5) * ampHi;
        data[i]     = Math.max(0, Math.min(255, data[i]     + n));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
      }
      ctx.putImageData(img, 0, 0);

      const blotchCount = 600;
      const ampLo = amplitude * 0.6;
      for (let i = 0; i < blotchCount; i++) {
        const x = Math.random() * SIZE;
        const y = Math.random() * SIZE;
        const r = 8 + Math.random() * 28;
        const t = (Math.random() - 0.5) * ampLo;
        ctx.fillStyle = `rgba(${t < 0 ? 0 : 255},${t < 0 ? 0 : 255},${t < 0 ? 0 : 255},${Math.abs(t)})`;
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      }
    }

    const warmGrad = ctx.createRadialGradient(SIZE * 0.4, SIZE * 0.22, SIZE * 0.05, SIZE * 0.4, SIZE * 0.22, SIZE * 0.9);
    warmGrad.addColorStop(0.0, 'rgba(255,250,240,0.10)');
    warmGrad.addColorStop(0.6, 'rgba(255,250,240,0.02)');
    warmGrad.addColorStop(1.0, 'rgba(0,0,0,0)');
    ctx.fillStyle = warmGrad;
    ctx.fillRect(0, 0, SIZE, SIZE);

    if (vignetteStrength > 0) {
      const vg = ctx.createRadialGradient(SIZE * 0.5, SIZE * 0.5, SIZE * 0.35, SIZE * 0.5, SIZE * 0.5, SIZE * 0.78);
      vg.addColorStop(0.0, 'rgba(0,0,0,0)');
      vg.addColorStop(1.0, `rgba(0,0,0,${vignetteStrength})`);
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, SIZE, SIZE);
    }

    return new CanvasTexture(c);
  }

  window.PreviewEnhance = { applyToInstance: applyToInstance };
})();
